import { useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AlertTriangle, ShieldAlert, Archive, RotateCcw } from "lucide-react";
import { useDeleteEstimate, useVoidEstimate, useArchiveEstimate, canHardDeleteEstimate } from "@/hooks/useEstimateLifecycle";

export type LifecycleEstimate = {
  id: string;
  estimate_number?: string | null;
  status?: string;
  approved_at?: string | null;
  signature_data?: string | null;
  signed_pdf_path?: string | null;
  project_id?: string | null;
  sent_at?: string | null;
  voided_at?: string | null;
  archived_at?: string | null;
};

type Mode = "delete" | "void" | "archive" | "restore";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: Mode;
  estimate: LifecycleEstimate | null;
  onCompleted?: () => void;
}

export function EstimateLifecycleDialog({ open, onOpenChange, mode, estimate, onCompleted }: Props) {
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const del = useDeleteEstimate();
  const voidM = useVoidEstimate();
  const arch = useArchiveEstimate();

  if (!estimate) return null;
  const num = estimate.estimate_number || "this estimate";
  const canDelete = canHardDeleteEstimate(estimate);

  const close = () => {
    setConfirmText("");
    setReason("");
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    try {
      if (mode === "delete") {
        await del.mutateAsync(estimate.id);
      } else if (mode === "void") {
        await voidM.mutateAsync({ id: estimate.id, reason });
      } else if (mode === "archive") {
        await arch.mutateAsync({ id: estimate.id, archive: true });
      } else if (mode === "restore") {
        await arch.mutateAsync({ id: estimate.id, archive: false });
      }
      onCompleted?.();
      close();
    } catch {/* toast handled in hook */}
  };

  const busy = del.isPending || voidM.isPending || arch.isPending;

  // ─── Delete mode ───
  if (mode === "delete") {
    if (!canDelete) {
      return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-amber-600" /> Cannot delete this estimate
              </AlertDialogTitle>
              <AlertDialogDescription>
                This estimate has been signed, accepted, sent, or connected to billing.
                It cannot be permanently deleted. You can <strong>void</strong> or <strong>archive</strong> it instead to preserve audit history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Close</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    }
    const confirmed = confirmText.trim().toUpperCase() === "DELETE";
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" /> Delete {num}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The estimate and its line items will be permanently removed.
              Type <strong>DELETE</strong> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm</Label>
            <Input id="confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" autoFocus />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button variant="destructive" disabled={!confirmed || busy} onClick={handleConfirm}>
              {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // ─── Void mode ───
  if (mode === "void") {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" /> Void {num}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Voiding marks the estimate as <strong>Voided</strong>. It can no longer be signed, sent, or converted.
              The public link will stop working. A void reason is required for the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reason">Reason</Label>
            <Textarea id="reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this being voided?" rows={3} autoFocus />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button variant="destructive" disabled={!reason.trim() || busy} onClick={handleConfirm}>
              {busy ? "Voiding…" : "Void estimate"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // ─── Archive / Restore mode ───
  const isRestore = mode === "restore";
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {isRestore ? <RotateCcw className="h-5 w-5 text-emerald-600" /> : <Archive className="h-5 w-5 text-muted-foreground" />}
            {isRestore ? "Restore" : "Archive"} {num}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isRestore
              ? "This estimate will be restored to the default list."
              : "This estimate will be hidden from the default list. Admins can find and restore it via the Archived filter."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button disabled={busy} onClick={handleConfirm}>
            {busy ? "Working…" : isRestore ? "Restore" : "Archive"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
